
import { supabase } from '@/integrations/supabase/client';
import { useCallback, useEffect, useRef } from 'react';

// Types for analytics events
type AnalyticsEvent = {
  actionType: string;
  metadata: Record<string, any>;
  timestamp: string;
};

// Only log these important actions to reduce quota usage
const IMPORTANT_ACTIONS = new Set([
  'video_completion',
  'video_play', 
  'app_session_start',
  'app_session_end',
  'time_lapse_click'
]);

// Global event queue and batch timer
let eventQueue: AnalyticsEvent[] = [];
let batchTimer: NodeJS.Timeout | null = null;
let isFlushingEvents = false;

// Runtime flags to avoid spamming network if RPC is missing
let isBatchAvailable = true;
let hasWarnedBatchUnavailable = false;
// Flush events to server using Edge Function
const flushEvents = async () => {
  if (eventQueue.length === 0 || isFlushingEvents || !isBatchAvailable) return;
  
  isFlushingEvents = true;
  const eventsToSend = [...eventQueue];
  eventQueue = []; // Clear queue immediately
  
  try {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Analytics timeout')), 5000);
    });

    // Send to Edge Function for maximum efficiency
    const analyticsPromise = fetch('https://cxplnvfimnyjiyhjwvnb.supabase.co/functions/v1/log-analytics', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || ''}`
      },
      body: JSON.stringify({ events: eventsToSend })
    });

    await Promise.race([analyticsPromise, timeoutPromise]);
  } catch (error) {
    // If edge function fails, disable analytics for this session
    isBatchAvailable = false;
    eventQueue = [];
    if (!hasWarnedBatchUnavailable) {
      console.warn('Analytics disabled: Edge function not available.', error);
      hasWarnedBatchUnavailable = true;
    }
    // Fail silently - don't break app for analytics
  } finally {
    isFlushingEvents = false;
  }
};

// Schedule batch sending
const scheduleBatch = () => {
  if (!isBatchAvailable) return;
  if (batchTimer) clearTimeout(batchTimer);
  
  batchTimer = setTimeout(() => {
    flushEvents();
  }, 30000); // Send every 30 seconds
};

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushEvents);
  window.addEventListener('pagehide', flushEvents);
}

export const useAnalytics = () => {
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
      // Flush remaining events when component unmounts
      if (eventQueue.length > 0) {
        flushEvents();
      }
    };
  }, []);

  const logAction = useCallback((
    actionType: string,
    metadata: Record<string, any> = {}
  ) => {
    // Only log important actions to reduce quota usage
    if (!IMPORTANT_ACTIONS.has(actionType)) {
      return null;
    }

    const event: AnalyticsEvent = {
      actionType,
      metadata: {
        ...metadata,
        client_timestamp: new Date().toISOString()
      },
      timestamp: new Date().toISOString()
    };

    eventQueue.push(event);
    
    // Schedule batch if we have events
    if (eventQueue.length === 1) {
      scheduleBatch();
    }

    // Flush immediately if queue gets too large
    if (eventQueue.length >= 50) {
      flushEvents();
    }

    return null;
  }, []);

  // Video-specific analytics functions
  const logVideoPlay = async (videoData: {
    post_id: string;
    video_url?: string;
    crop_type?: string;
    user_name?: string;
    autoplay?: boolean;
    timestamp?: number;
  }) => {
    return logAction('video_play', {
      ...videoData,
      timestamp: videoData.timestamp ? Math.round(videoData.timestamp / 1000) : undefined, // Convert to seconds
      played_at: new Date().toISOString()
    });
  };

  const logVideoPause = async (videoData: {
    post_id: string;
    video_url?: string;
    watch_duration: number;
    timestamp?: number;
  }) => {
    return logAction('video_pause', {
      ...videoData,
      watch_duration: Math.round(videoData.watch_duration / 1000), // Convert to seconds
      timestamp: videoData.timestamp ? Math.round(videoData.timestamp / 1000) : undefined, // Convert to seconds
      paused_at: new Date().toISOString()
    });
  };

  const logVideoMuteToggle = async (videoData: {
    post_id: string;
    video_url?: string;
    is_muted: boolean;
    timestamp?: number;
  }) => {
    return logAction(videoData.is_muted ? 'video_mute' : 'video_unmute', {
      ...videoData,
      timestamp: videoData.timestamp ? Math.round(videoData.timestamp / 1000) : undefined, // Convert to seconds
      toggled_at: new Date().toISOString()
    });
  };

  const logVideoCompletion = async (videoData: {
    post_id: string;
    video_url?: string;
    total_duration: number;
    watch_duration: number;
    completion_percentage: number;
    crop_type?: string;
  }) => {
    return logAction('video_completion', {
      ...videoData,
      total_duration: Math.round(videoData.total_duration / 1000), // Convert to seconds
      watch_duration: Math.round(videoData.watch_duration / 1000), // Convert to seconds
      completed_at: new Date().toISOString()
    });
  };

  const logVideoSkip = async (videoData: {
    post_id: string;
    video_url?: string;
    watch_duration: number;
    skip_timestamp: number;
    total_duration?: number;
  }) => {
    return logAction('video_skip', {
      ...videoData,
      watch_duration: Math.round(videoData.watch_duration / 1000), // Convert to seconds
      skip_timestamp: Math.round(videoData.skip_timestamp / 1000), // Convert to seconds
      total_duration: videoData.total_duration ? Math.round(videoData.total_duration / 1000) : undefined, // Convert to seconds
      skipped_at: new Date().toISOString()
    });
  };

  const logVideoEngagement = async (engagementData: {
    post_id: string;
    video_url?: string;
    engagement_type: 'scroll_to_view' | 'intersect_start' | 'intersect_end' | 'full_watch' | 'replay';
    watch_duration?: number;
    intersection_ratio?: number;
    viewport_time?: number;
  }) => {
    return logAction('video_engagement', {
      ...engagementData,
      watch_duration: engagementData.watch_duration ? Math.round(engagementData.watch_duration / 1000) : undefined, // Convert to seconds
      viewport_time: engagementData.viewport_time ? Math.round(engagementData.viewport_time / 1000) : undefined, // Convert to seconds
      engagement_at: new Date().toISOString()
    });
  };

  return {
    logAction,
    logVideoPlay,
    logVideoPause,
    logVideoMuteToggle,
    logVideoCompletion,
    logVideoSkip,
    logVideoEngagement
  };
};
